import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { throttle } from 'lodash';

import { getPassiveHandlerDefaultOptions } from 'utils/detectPassiveEvents';

import PageEvents from '../models/PageEvents';
import wa from 'apps/analytics/services/waEventEmitter';

import ScribeLink from 'lib/apps/scribe/ScribeLink';

export default class TilesDropdown extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    defaultBtnText: PropTypes.string,
    dropdown: PropTypes.shape({
      links: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
        })
      ),
      tiles: PropTypes.arrayOf(PropTypes.shape({
        buttonText: PropTypes.string,
        description: PropTypes.string,
        imageUrl: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
      })),
    }).isRequired,
    dropdownLabel: PropTypes.string,
    primaryBarRef: PropTypes.object,
    itemsLimit: PropTypes.number,
  };

  state = {
    dropdownWidth: 0,
  };

  get calculatedStyles() {
    const { dropdownWidth } = this.state;
    return {
      width: `${dropdownWidth}px`,
    };
  }

  get tiles() {
    const { dropdown: { links = [], tiles = [] } = {}, itemsLimit } = this.props;

    if (!itemsLimit) {
      return tiles;
    }

    const hasNavigation = links && links.length > 0;
    const limit = hasNavigation && itemsLimit - 1 || itemsLimit;

    if (tiles && tiles.length > limit) {
      return tiles.slice(0, limit);
    }

    return tiles;
  }

  calculateDropdownWidth = () => {
    const { dropdown = {}, primaryBarRef, itemsLimit } = this.props;

    if (primaryBarRef && primaryBarRef.current) {
      const { links = [] } = dropdown;
      const hasNavigation = links && links.length > 0;

      // Navigation is considered to be a separate tile so should be included in count
      const itemsCount = hasNavigation + this.tiles.length;
      let dropdownWidth = primaryBarRef.current.clientWidth;

      if (itemsLimit && itemsCount < itemsLimit) {
        dropdownWidth = Math.round(primaryBarRef.current.clientWidth / itemsLimit * itemsCount);
      }

      this.setState({ dropdownWidth });
    }
  }

  handleWindowResize = throttle(() => {
    this.calculateDropdownWidth();
  }, 100);

  getAnalyticsHandler = (event, data) => () => {
    wa.triggerPageEventWithDescription(event, data);
    this.props.analyticsEventSender({
      menuMainItem: data.DROPDOWN_LABEL,
      subMenuSection: data.SECTION,
      subMenuSectionItem: data.TEXT || data.ALT_TEXT,
    });
  }

  getNavigation = () => {
    const { dropdown: { links: navigation = [] } = {}, dropdownLabel = '' } = this.props;
    const hasNavigation = navigation && navigation.length > 0;

    if (!hasNavigation) {
      return null;
    }

    return (
      <li className="hd-tiles__item hd-tiles__navigation">
        <ul className="section-items">
          {navigation.map(item => (
            <li
              key={item.text}
              className="section-item"
            >
              <Link
                className="section-link"
                to={item.url}
                onClick={this.getAnalyticsHandler(
                  PageEvents.TILES_NAV_ITEM_CLICKED,
                  {
                    DROPDOWN_LABEL: dropdownLabel,
                    SECTION: 'Left pane',
                    TEXT: item.text,
                    LINK: item.url,
                  }
                )}
              >
                {item.text}
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize, getPassiveHandlerDefaultOptions());
    this.calculateDropdownWidth();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize, getPassiveHandlerDefaultOptions());
  }

  render() {
    const { className = '', defaultBtnText, dropdownLabel = '' } = this.props;

    return (
      <div className={`hd-tiles ${className}`} style={this.calculatedStyles}>
        <ul className="hd-tiles__list">
          { this.getNavigation() }
          { this.tiles.map((tile, index) => {
            const inKey = `${tile.name}${index}`;
            const buttonText = tile.buttonText || defaultBtnText;
            const altText = tile.description || tile.buttonText;

            return (
              <li
                key={inKey}
                className="hd-tiles__item"
              >
                <ScribeLink
                  className="hd-tiles__imgLink"
                  to={tile.link}
                  onClick={this.getAnalyticsHandler(
                    PageEvents.TILES_BANNER_CLICKED,
                    {
                      DROPDOWN_LABEL: dropdownLabel,
                      SECTION: 'Creative',
                      ALT_TEXT: altText,
                      LINK: tile.link,
                    }
                  )}
                >
                  <img className="hd-tiles__img" src={tile.imageUrl} alt={altText} />
                </ScribeLink>

                {tile.description && (
                  <p className="hd-tiles__description">
                    {tile.description}
                  </p>
                )}

                { buttonText && (
                  <ScribeLink
                    className="button hd-tiles__btn"
                    to={tile.link}
                    onClick={this.getAnalyticsHandler(
                      PageEvents.TILES_BUTTON_CLICKED,
                      {
                        DROPDOWN_LABEL: dropdownLabel,
                        SECTION: 'Shop now',
                        TEXT: buttonText,
                        LINK: tile.link,
                      }
                    )}
                  >
                    {buttonText}
                  </ScribeLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}
